<template>
   <div data-cy="claim-code-generator-v1" style="min-height: 18rem">
      <div class="flex flex-col items-center">
         <RichTextRenderer
            v-if="block.data.generateCodeContent"
            data-cy="richtext-generate-code"
            :document="block.data.generateCodeContent"
         />
         <DateInputs
            :id="block.id"
            :isFormatDDMMYY="props.block.data.dateOfBirth.displayFormat === 'dd-mm-yyyy'"
            isRequired
            :validations="dateOfBirthValidation"
            :showBlockErrors="state.dateOfBirth.showErrors"
            :existingAnswer="{
               year: state.dateOfBirth.input.year,
               month: state.dateOfBirth.input.month,
               day: state.dateOfBirth.input.day,
            }"
            :is-disabled="state.isDisabled"
            @inputs-update="processDateOfBirth"
         />
         <LoadingButton
            data-cy="generate-code-button"
            class="float-right mt-6"
            size="sm"
            rounded-edges="md"
            :is-loading="state.isLoading"
            :is-disabled="state.isDisabled"
            :text="$t('survey.blocks.claim_flow_generator.button_submit')"
            @click="generateCode"
         />
      </div>
      <div v-if="state.claimCode" class="mt-6 flex flex-col items-start justify-start">
         <RichTextRenderer data-cy="richtext-display-code" :document="block.data.displayCodeContent" />
         <div class="mt-2 flex w-full flex-col items-center justify-center">
            <div class="flex rounded-md border p-4 text-black" data-cy="claim-code">{{ state.claimCode }}</div>
         </div>
         <CheckboxItem
            id="confirm"
            name="confirm"
            class="mt-6 w-full"
            :label="block.data.confirmationLabel"
            :checked="state.hasConfirmed"
            @change="onCheckboxChange"
         />
         <InvalidInputsList
            class="mb-6 w-full"
            :error-list="state.checkboxErrors"
            :class="{ hidden: !showBlockErrors }"
         />
      </div>
   </div>
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { dateInputsEmits } from "../_inputs/DateInputs.vue";
import { getFormattedFromDateInput } from "../date/date-utils";
import AnswersAPI, { type AnswerResp } from "@/data/api-layer/answersApi";
import { useFlowParticipationStore } from "@/stores/flowParticipationStore";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { RichText } from "@/types";

export interface ClaimCodeGeneratorAnswerData {
   hasConfirmed: boolean;
}

export interface ClaimCodeGeneratorBlock extends FlowselBlock {
   data: {
      generateCodeContent: RichText;
      displayCodeContent: RichText;
      dateOfBirth: {
         displayFormat: "dd-mm-yyyy" | "yyyy-mm-dd";
      };
      confirmationLabel: string;
   };
}

// declarations
// -----------------------------------------
const emit = defineEmits(["update-valid", "update-answer"]);
const { t: $t } = useI18n();
const flowParticipationStore = useFlowParticipationStore();
const dateOfBirthValidation = { min: (new Date().getFullYear() - 150).toString(), max: "today" };
const DATE_FORMAT = "YYYY-MM-DD";

const props = defineProps<{
   userBlockAnswer: AnswerResp | null;
   block: ClaimCodeGeneratorBlock;
   showBlockErrors: boolean;
}>();

// state
const state = reactive({
   dateOfBirth: {
      input: {
         day: "",
         month: "",
         year: "",
      },
      isValid: false,
      showErrors: false,
   },
   isLoading: false,
   hasConfirmed: !!props.userBlockAnswer?.data?.hasConfirmed,
   checkboxErrors: null,
   isDisabled: !!props.userBlockAnswer?.metadata?.claimCode,
   claimCode: props.userBlockAnswer?.metadata?.claimCode,
});

// Methods
// -----------------------------------------
function processDateOfBirth(inputsInfo: dateInputsEmits) {
   state.dateOfBirth.isValid = inputsInfo.valid;

   state.dateOfBirth.input = {
      year: inputsInfo.year,
      month: inputsInfo.month,
      day: inputsInfo.day,
   };
}

async function generateCode() {
   if (!state.dateOfBirth.isValid) {
      state.dateOfBirth.showErrors = true;
      return;
   }

   state.isLoading = true;
   const dateOfBirthAnswer = getFormattedFromDateInput(state.dateOfBirth.input, DATE_FORMAT);
   try {
      // submit DoB as part of the answer and receive back the claim code
      const response = await AnswersAPI.submitWithToken(
         props.block?.blockVersionId,
         { personal_identifier: dateOfBirthAnswer },
         flowParticipationStore.token
      );

      const { claimCode } = response.data.metadata;
      if (claimCode) {
         state.claimCode = claimCode;
      }
      // Disallow generating a code a second time
      state.isDisabled = true;
   } catch (error) {
      console.error(error);
   } finally {
      state.isLoading = false;
   }
}

function onCheckboxChange() {
   state.hasConfirmed = !state.hasConfirmed;

   updateAnswer();
}

function updateAnswer() {
   if (state.claimCode && state.hasConfirmed) {
      emit("update-valid", true);
      emit("update-answer", { has_confirmed: state.hasConfirmed });
      state.checkboxErrors = null;
   } else {
      emit("update-valid", false);
      state.checkboxErrors = [
         {
            inputId: props.block.id,
            validator: "required",
            message: $t("survey.blocks.claim_flow_generator.confirm_required_message"),
         },
      ];
   }
}

updateAnswer();
</script>
